import React, { useMemo } from 'react';
import { HbrDomTable } from '@harbor/elements-react-standalone';
import {
  Column,
  ColumnAlignment,
  ColumnType,
  DataType,
  getColumnTypeConfig,
} from '../../../types/table';
import { OrgChartRow, getDirectReports } from '../../../support/orgScope';
import { ACTIVITY_CATEGORIES, type ActivityCounts } from '../../../support/salesforceTeamActivity';

interface SummaryNode {
  email?: string;
  is_manager?: boolean;
  own_total_opportunities?: number;
  total_opportunities?: number;
  reports?: Record<string, SummaryNode>;
}

interface Props {
  orgRows: OrgChartRow[];
  managerEmail: string;
  reportSummariesByEmail: Record<string, SummaryNode>;
  /** Per-activity-type counts (each report's rollup); null when the server can't supply them. */
  activityByEmail: Map<string, ActivityCounts> | null;
  /** The activity counts are still loading. */
  activityLoading?: boolean;
  onSelectEmail: (email: string) => void;
}

type RowModel = {
  id: string;
  email: string;
  is_manager: boolean;
  opportunities: number;
} & Record<string, string | number | boolean>;

// Column ids for the activity types ("POC/POV" and friends contain characters unsafe in slot names).
const activityColumnId = (category: string) => `act_${category.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;

const COLUMNS: Array<{ id: string; name: string; width: string; title: (mgr: boolean) => string }> = [
  {
    id: 'opportunities',
    name: 'Opportunities',
    width: '10%',
    title: (mgr) => (mgr ? 'Opportunities the team is tagged to' : 'Opportunities tagged to'),
  },
  ...ACTIVITY_CATEGORIES.map((category) => ({
    id: activityColumnId(category),
    name: category,
    width: `${(68 / ACTIVITY_CATEGORIES.length).toFixed(2)}%`,
    title: (mgr: boolean) => (mgr ? `Team ${category} activities (sub-teams included)` : `${category} activities`),
  })),
];

function safeIdFromEmail(email: string) {
  return `u_${email.toLowerCase().replace(/[^a-z0-9_-]/g, '_')}`;
}

/**
 * One row per direct report: the opportunities they are tagged to and a column for every activity type
 * (POC/POV, Demo, Workshop, Design/Whiteboard, Briefing/EBC, Trial, Other) for the selected time period. A
 * sub-manager's row is their whole team's rollup (themselves plus everyone under them); an IC's row is their
 * own. Clicking a name drills in.
 */
export default function TeamMembersTable({
  orgRows,
  managerEmail,
  reportSummariesByEmail,
  activityByEmail,
  activityLoading = false,
  onSelectEmail,
}: Props) {
  const normalizedManagerEmail = String(managerEmail || '').toLowerCase();

  const reports = useMemo(
    () => getDirectReports(orgRows, normalizedManagerEmail),
    [orgRows, normalizedManagerEmail],
  );

  const data: RowModel[] = useMemo(() => {
    // While loading show an ellipsis; if the server can't supply activity counts show "—", never a false 0.
    const cell = (n: number | undefined) => (activityLoading ? '…' : n == null ? '—' : String(n));
    return reports.map((r) => {
      const email = String(r.user_email || '').toLowerCase();
      const summary = reportSummariesByEmail[email];
      const counts = activityByEmail?.get(email);
      const row: RowModel = {
        id: safeIdFromEmail(email),
        email,
        is_manager: !!r.is_manager,
        opportunities: Number(summary?.total_opportunities ?? 0),
      };
      for (const category of ACTIVITY_CATEGORIES) row[activityColumnId(category)] = cell(counts?.[category]);
      return row;
    });
  }, [reports, reportSummariesByEmail, activityByEmail, activityLoading]);

  const visibleColumns: Column[] = useMemo(() => {
    // Percentage widths (summing to 100%) rather than fixed px: HbrDomTable's table is width:100%, so
    // percent columns shrink/grow WITH the container and never force horizontal scroll.
    const base: Column[] = [
      {
        id: 'email',
        name: 'Team Member',
        width: '22%',
        columnType: ColumnType.Custom,
        sortable: false,
        align: ColumnAlignment.Left,
        dataType: DataType.String,
        resizable: false,
        reorderable: false,
      },
      ...COLUMNS.map((c) => ({
        id: c.id,
        name: c.name,
        width: c.width,
        columnType: ColumnType.Custom,
        sortable: false,
        align: ColumnAlignment.Right,
        dataType: DataType.String,
        resizable: false,
        reorderable: false,
      })),
    ];
    return base.map((c) => getColumnTypeConfig(c)).map((c) => ({ ...c, resizable: false, reorderable: false }));
  }, []);

  const renderHeader = () =>
    visibleColumns.map((column) => (
      <div key={`column-${column.id}`} slot={`column-${column.id}`}>
        <div
          className="flex items-center relative"
          style={{ justifyContent: column.align === ColumnAlignment.Right ? 'flex-end' : 'flex-start' }}
        >
          <div>{column.name}</div>
        </div>
      </div>
    ));

  const renderBody = () => {
    if (!data.length) return null;

    const list: JSX.Element[] = [];

    for (const row of data) {
      for (const column of visibleColumns) {
        const slot = `row-${row.id}-column-${column.id}`;

        if (column.id === 'email') {
          list.push(
            <div
              key={slot}
              slot={slot}
              className="min-h-8 min-w-0 flex items-center font-semibold hover:text-link hover:underline cursor-pointer truncate"
              title={`${row.email}${row.is_manager ? ' (Mgr)' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelectEmail(row.email);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectEmail(row.email);
                }
              }}
              aria-label={`View ${row.email}`}
            >
              {row.email}
              {row.is_manager ? ' (Mgr)' : ''}
            </div>,
          );
          continue;
        }

        list.push(
          <div
            key={slot}
            slot={slot}
            className="min-h-8 flex items-center box-content"
            style={{ justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}
            title={COLUMNS.find((c) => c.id === column.id)?.title(row.is_manager)}
          >
            {(row as any)[column.id]}
          </div>,
        );
      }
    }

    return list;
  };

  if (!reports.length) {
    return <div className="hbr-css__text-secondary-sm">No direct reports found.</div>;
  }

  return (
    <HbrDomTable
      autoLayout={true}
      columns={visibleColumns}
      data={data}
      style={{ display: 'block', width: '100%' }}
    >
      {renderHeader()}
      {renderBody()}
    </HbrDomTable>
  );
}
